// Canonical multi-ad-group ad workspace: shape, normalization, and Google Ads limit
// validation. ONE structure everywhere — `payload.adGroups` is always an array (length
// >= 1). This is the single source of truth for the preview, the confirmation gate,
// and (mirrored, inlined) the push edge function.

// ── Google Ads hard limits (validate content BEFORE it can be pushed) ──
export const HEADLINE_MAX = 30;   // chars per headline
export const DESC_MAX = 90;       // chars per description
export const HEADLINES_MIN = 3;
export const HEADLINES_MAX = 15;
export const DESCRIPTIONS_MIN = 2;
export const DESCRIPTIONS_MAX = 4;
export const ADS_PER_GROUP_MAX = 3;
export const SITELINK_TEXT_MAX = 25;   // chars, sitelink link text
export const SITELINK_DESC_MAX = 35;   // chars, each sitelink description line
export const SITELINKS_PER_GROUP = 4;  // default sitelinks per ad group

export type MatchType = "Exact" | "Phrase" | "Broad";
export type RSA = { headlines: string[]; descriptions: string[]; url: string; impact: string };
// "specific" = a sub-angle of the ad group's own service; "main" = another real service
// the business offers (or Contact Us). Every URL comes from the verified page inventory.
export type Sitelink = { text: string; url: string; kind: "specific" | "main" };
export type AdGroupSpec = {
  adGroupName: string;
  keywords: { text: string; matchType: MatchType }[];
  ads: RSA[];
  sitelinks: Sitelink[];
  // How many model-proposed sitelinks were dropped because no real inventory page backed
  // them (surfaced as a small note in the preview). 0/undefined = nothing skipped.
  droppedSitelinks?: number;
};

// ── Verified page inventory (from get-site-pages; sitemap-derived) ──
// The ONLY source of real sitelink URLs. Sitelinks may use nothing outside this.
export type SiteInventoryPage = { label: string; url: string; location?: string };
export type SiteInventory = {
  domain: string;
  homepage?: { url: string } | null;
  contact?: { url: string } | null;
  services: SiteInventoryPage[];
  locations: SiteInventoryPage[];
  source?: "sitemap" | "fallback" | "none";
};

function normMatch(m: unknown): MatchType {
  const s = String(m ?? "Phrase").toLowerCase();
  if (s.startsWith("ex")) return "Exact";
  if (s.startsWith("br")) return "Broad";
  return "Phrase";
}

// Exported: AriaTab normalizes a flat sitelink list from an AI response with it.
export const asArr = (v: unknown): any[] => (Array.isArray(v) ? v : []);

function normKeyword(k: any) {
  return { text: String(k?.text ?? k?.kw ?? "").trim(), matchType: normMatch(k?.matchType ?? k?.match) };
}

// ACCOUNT STANDARD: every keyword ships as BOTH Exact and Phrase — never Broad, never a
// single match type. Enforced DETERMINISTICALLY here so the result never depends on the
// model's (or a typed toggle's) chosen match type. Dedupe by text (case-insensitive); each
// distinct keyword text becomes exactly two entries (Exact + Phrase). Idempotent.
export function expandMatchTypes(keywords: { text: string; matchType: MatchType }[]): { text: string; matchType: MatchType }[] {
  const seen = new Set<string>();
  const out: { text: string; matchType: MatchType }[] = [];
  for (const k of asArr(keywords)) {
    const text = String(k?.text ?? "").trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ text, matchType: "Exact" });
    out.push({ text, matchType: "Phrase" });
  }
  return out;
}
function normAd(a: any): RSA {
  return {
    headlines: asArr(a?.headlines).map((h) => String(h)).filter((h) => h.trim()),
    descriptions: asArr(a?.descriptions).map((d) => String(d)).filter((d) => d.trim()),
    url: String(a?.url ?? "").trim(),
    impact: String(a?.impact ?? "").trim(),
  };
}
function normGroup(g: any, i: number, inv?: SiteInventory | null): AdGroupSpec {
  const adGroupName = (String(g?.adGroupName ?? g?.name ?? "").trim()) || `Ad group ${i + 1}`;
  const { sitelinks, dropped } = resolveSitelinks(g, inv, adGroupName);
  return {
    adGroupName,
    keywords: expandMatchTypes(asArr(g?.keywords).map(normKeyword)),
    ads: asArr(g?.ads).map(normAd),
    sitelinks,
    droppedSitelinks: dropped,
  };
}

// Coerce ANY incoming ad payload to the canonical adGroups[] — canonical, the
// model's improvised top-level array, or a legacy flat single ad — so nothing
// downstream ever sees an unexpected structure. `inv` is the verified page inventory
// that gates every sitelink URL (see resolveSitelinkUrl).
export function normalizeAdGroups(payload: any, inv?: SiteInventory | null): AdGroupSpec[] {
  if (!payload) return [];
  if (Array.isArray(payload.adGroups)) return payload.adGroups.map((g: any, i: number) => normGroup(g, i, inv));
  if (Array.isArray(payload)) return payload.map((g: any, i: number) => normGroup(g, i, inv));
  if (Array.isArray(payload.headlines) || Array.isArray(payload.descriptions)) {
    return [normGroup({ adGroupName: payload.adGroupName ?? payload.title, keywords: payload.keywords, ads: [payload], sitelinks: payload.sitelinks }, 0, inv)];
  }
  return [];
}

// ── Sitelinks: VERIFIED-ONLY. Every URL must be a real page in the inventory; a URL is
// never built from the sitelink's name. Invention is structurally impossible. ──
const PLACEHOLDER_URL = /example\.com|your-?domain|\[|^#|localhost/i;

const slugify = (s: unknown): string =>
  String(s ?? "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const deslug = (s: unknown): string =>
  String(s ?? "").split(/[-_]+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

// Canonical form for URL identity (host w/o www, no trailing slash, lowercased).
function normUrl(u: unknown): string {
  const raw = String(u ?? "").trim();
  try {
    const x = new URL(raw);
    return `${x.protocol}//${x.hostname.replace(/^www\./, "")}${x.pathname.replace(/\/+$/, "")}`.toLowerCase();
  } catch {
    return raw.toLowerCase().replace(/\/+$/, "");
  }
}

// Every real page in the inventory (services + locations + contact + homepage).
export function inventoryPages(inv: SiteInventory): SiteInventoryPage[] {
  const all: SiteInventoryPage[] = [...asArr(inv.services), ...asArr(inv.locations)];
  if (inv.contact?.url) all.push({ label: "Contact Us", url: inv.contact.url });
  if (inv.homepage?.url) all.push({ label: "Home", url: inv.homepage.url });
  return all.filter((p) => p.url);
}
function inventoryUrlMap(inv: SiteInventory): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of inventoryPages(inv)) { const n = normUrl(p.url); if (!m.has(n)) m.set(n, p.url); }
  return m;
}

// THE ENFORCEMENT GATE. Resolve a sitelink to a REAL inventory URL, or "" (drop it).
// No slugify, ever. With an inventory: the model's URL must be an inventory page, else we
// try to match its text to a page, else drop. Without an inventory: keep a real URL as-is
// (already validated at generation) but never fabricate one.
export function resolveSitelinkUrl(text: string, url: unknown, inv?: SiteInventory | null): string {
  const clean = String(url ?? "").trim();
  if (inv) {
    const map = inventoryUrlMap(inv);
    if (clean) { const canon = map.get(normUrl(clean)); if (canon) return canon; }
    const t = slugify(text);
    if (t) {
      const pages = inventoryPages(inv);
      for (const p of pages) if (slugify(p.label) === t) return p.url;              // exact label match
      for (const p of pages) {                                                      // guarded contains match
        const ps = slugify(p.label);
        if (ps && Math.min(ps.length, t.length) >= 4 && (ps.includes(t) || t.includes(ps))) return p.url;
      }
    }
    return "";
  }
  const isReal = /^https?:\/\//i.test(clean) && !PLACEHOLDER_URL.test(clean);
  return isReal ? clean : "";
}

function normSitelinkKind(k: unknown): "specific" | "main" {
  return String(k ?? "").toLowerCase() === "main" ? "main" : "specific";
}

// Normalize ONE raw sitelink against the verified inventory. Same per-item rules resolveSitelinks
// applies in bulk — critically it routes the URL through resolveSitelinkUrl, so a URL that is not a
// real page in the inventory comes back "" and the caller drops it. Sitelink URLs stay
// verified-only; invention remains structurally impossible.
export function normSitelink(s: unknown, inv?: SiteInventory | null): Sitelink {
  const raw = (s ?? {}) as { text?: unknown; title?: unknown; url?: unknown; kind?: unknown };
  const text = String(raw.text ?? raw.title ?? "").trim();
  return { text, url: resolveSitelinkUrl(text, raw.url, inv), kind: normSitelinkKind(raw.kind) };
}

// Real location tokens known to this site (from location labels + location-tagged services).
function locationTokens(inv: SiteInventory): Set<string> {
  const s = new Set<string>();
  for (const l of asArr(inv.locations)) for (const t of slugify(l.label).split("-")) if (t) s.add(t);
  for (const svc of asArr(inv.services)) if (svc.location) s.add(String(svc.location).toLowerCase());
  return s;
}

// ── HARD RULE: a sitelink can NEVER be a bare location name ("Newlands"). It must be a
// SERVICE phrase — "Newlands Locksmith" / "Locksmith Newlands". General logic, no place
// list: a bare location is text that contains a location token and NO service word. ──
const SL_STOP = new Set(["and","the","for","in","of","our","your","a","an","to","with","us","services","service","near","area","areas","local"]);
// The vocabulary that makes a sitelink a service phrase, plus the single most representative
// service word (most frequent across service labels, minus location tokens + stopwords).
function serviceWords(inv?: SiteInventory | null): { primary: string; all: Set<string> } {
  if (!inv) return { primary: "", all: new Set() };
  const locToks = locationTokens(inv);
  const freq = new Map<string, number>();
  for (const svc of asArr(inv.services)) for (const t of slugify(svc.label).split("-")) {
    if (!t || t.length < 3 || locToks.has(t) || SL_STOP.has(t)) continue;
    freq.set(t, (freq.get(t) || 0) + 1);
  }
  // The business's CORE service is usually in the domain ("pro247locksmith.com" → locksmith).
  // The domain is one glued blob ("pro247locksmith-com"), so test as a SUBSTRING, not a token.
  //
  // A SUBSTRING TEST ALSO MATCHES FRAGMENTS: "lock" is inside "pro247locksmith-com" just as
  // much as "locksmith" is. Scoring domain matches by FREQUENCY then let the fragment win —
  // "lock" appeared as a token in 7 service labels vs "locksmith" in 5, so it scored 107 vs 105
  // and every service-area sitelink read "Newlands Lock". "Lock" is not the profession.
  //
  // RULE: the domain names the PROFESSION, so among words the domain contains, the LONGEST one
  // is the profession — a shorter word is a fragment of it, never a better answer. Rank domain
  // matches by length; frequency only decides between words the domain does NOT contain.
  const domainStr = slugify(inv.domain);
  let primary = "", best = -1;
  for (const [w, c] of freq) {
    const score = domainStr.includes(w) ? 1000 + w.length : c;
    if (score > best || (score === best && w.length > primary.length)) { best = score; primary = w; }
  }
  return { primary: primary ? deslug(primary) : "", all: new Set(freq.keys()) };
}
// A sitelink text that is ONLY a location (has a location token, no service word) is invalid.
function isBareLocation(text: string, inv?: SiteInventory | null): boolean {
  if (!inv) return false;
  const toks = slugify(text).split("-").filter(Boolean);
  if (!toks.length) return false;
  const locToks = locationTokens(inv);
  const svc = serviceWords(inv).all;
  return toks.some((t) => locToks.has(t)) && !toks.some((t) => svc.has(t));
}
// Trim `text` to whole words that fit within `max`. Never cuts mid-word — it drops trailing
// words instead, so no sitelink ever ships a meaningless fragment ("Locksm", "Lock").
function fitWholeWords(text: string, max: number): string {
  const t = String(text || "").trim();
  if (t.length <= max) return t;
  let acc = "";
  for (const w of t.split(/\s+/)) {
    const next = acc ? `${acc} ${w}` : w;
    if (next.length > max) break;
    acc = next;
  }
  return acc;
}

// Combine a bare location with the site's primary service → "Newlands Locksmith". Returns ""
// only when the site has NO derivable service word (then the caller must drop it, never emit
// a bare place name). Respects the 25-char sitelink-text cap.
//
// RULE: on a service-area sitelink BOTH parts are whole words, and if it must be shortened the
// AREA gives way — the profession noun is the meaningful half and is never truncated. The old
// last resort sliced `"<area> <service>"` at 25 chars, which cut the service (it sits last).
function serviceizeLocation(text: string, inv?: SiteInventory | null): string {
  const svc = serviceWords(inv).primary;
  if (!svc) return "";
  const loc = text.trim();
  const a = `${loc} ${svc}`.trim();                     // "Newlands Locksmith"
  if (a.length <= SITELINK_TEXT_MAX) return a;
  const b = `${svc} ${loc}`.trim();                     // "Locksmith Newlands"
  if (b.length <= SITELINK_TEXT_MAX) return b;
  // Neither orientation fits: keep the profession WHOLE and shorten the AREA to whole words.
  if (svc.length >= SITELINK_TEXT_MAX) return svc.slice(0, SITELINK_TEXT_MAX).trim();  // pathological: profession alone overruns
  const shortLoc = fitWholeWords(loc, SITELINK_TEXT_MAX - svc.length - 1);
  return shortLoc ? `${shortLoc} ${svc}` : svc;
}

// The verified page that best represents an ad group's LOCATION, or null if the ad group
// isn't a location (or the site has no matching real location page → never invent).
export function findLocationPage(name: string, inv?: SiteInventory | null): SiteInventoryPage | null {
  if (!inv) return null;
  const nameSlug = slugify(name);
  if (!nameSlug) return null;
  const nameToks = nameSlug.split("-").filter(Boolean);
  const locs = asArr(inv.locations) as SiteInventoryPage[];
  // 1) a pure location page whose label matches the ad group's location
  let hit = locs.find((l) => slugify(l.label) === nameSlug);
  if (!hit) hit = locs.find((l) => { const ls = slugify(l.label); return ls && (nameSlug.includes(ls) || ls.includes(nameSlug)); });
  if (hit) return hit;
  // 2) no pure location page → a location-tagged service for a location token in the name
  const tokens = locationTokens(inv);
  const matchTok = nameToks.find((t) => tokens.has(t));
  if (matchTok) {
    const tagged = (asArr(inv.services) as SiteInventoryPage[]).filter((s) => String(s.location).toLowerCase() === matchTok);
    if (tagged.length) { tagged.sort((a, b) => a.label.length - b.label.length); return tagged[0]; }
  }
  return null;
}

// Resolve an ad group's sitelinks against the inventory: drop anything not backed by a real
// page (counting drops), de-dupe destinations, cap at 4. Order is PRESERVED as given —
// normalization never reorders or re-inserts sitelinks (see below).
function resolveSitelinks(container: any, inv: SiteInventory | null | undefined, adGroupName: string): { sitelinks: Sitelink[]; dropped: number } {
  const arr = Array.isArray(container?.sitelinks) ? container.sitelinks : (container?.payload?.sitelinks ?? []);
  let dropped = 0;
  const seen = new Set<string>();
  const out: Sitelink[] = [];
  for (const s of arr) {
    const text = String(s?.text ?? s?.title ?? "").trim();
    if (!text) continue;
    const url = resolveSitelinkUrl(text, s?.url, inv);
    if (!url) { dropped++; continue; }
    const key = normUrl(url);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ text, url, kind: normSitelinkKind(s?.kind) });
  }
  // NO LOCATION-FIRST PIN — rule REVOKED (deliberate product decision, not a bug fix).
  // This used to splice the ad group's location page out of wherever it sat and unshift it to
  // index 0 on EVERY normalization. Because normalization runs on every render, it silently
  // reverted any regeneration of the FIRST sitelink (the swap landed, then the pin put the
  // location page back at index 0) and the slice(0, 4) below then dropped the 4th sitelink.
  // Sitelinks now keep whatever order they are given; normalization must not reorder or
  // re-insert them. Do not reintroduce this pin.
  // FINAL GUARD — no sitelink may ship as a bare location, whatever produced it. Serviceize
  // it; drop it only when the site has no derivable service word (never emit "Newlands").
  const guarded: Sitelink[] = [];
  for (const s of out) {
    if (isBareLocation(s.text, inv)) {
      const fixed = serviceizeLocation(s.text, inv);
      if (!fixed) { dropped++; continue; }
      // serviceizeLocation already fits within the cap without cutting the profession; this is a
      // whole-word belt, never a mid-word slice.
      guarded.push({ ...s, text: fitWholeWords(fixed, SITELINK_TEXT_MAX) || fixed });
    } else guarded.push(s);
  }
  return { sitelinks: guarded.slice(0, SITELINKS_PER_GROUP), dropped };
}

// PER-SITELINK REGENERATION — pick ONE valid replacement sitelink for an ad group: a real
// inventory page not already used here, built into a valid, service-phrase (never bare
// location) sitelink whose text isn't already present. Deterministic (no AI round-trip, so
// nothing can flip the workspace), guaranteed-real URL. Returns null when nothing new fits.
export function pickReplacementSitelink(
  adGroupName: string, inv: SiteInventory | null | undefined, existing: Sitelink[],
): Sitelink | null {
  if (!inv) return null;
  const usedUrls = new Set(asArr(existing).map((s) => normUrl(s.url)));
  const usedTexts = new Set(asArr(existing).map((s) => String(s.text || "").trim().toLowerCase()));
  const pool = inventoryPages(inv).filter((p) => p.url && !usedUrls.has(normUrl(p.url)));
  // Shuffle so repeated taps cycle through different pages rather than always the same one.
  for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [pool[i], pool[j]] = [pool[j], pool[i]]; }
  const locUrls = new Set(asArr(inv.locations).map((l) => normUrl(l.url)));
  for (const p of pool) {
    let text = p.label.length <= SITELINK_TEXT_MAX ? p.label : deslug(slugify(p.label).split("-").slice(0, 3).join("-"));
    if (isBareLocation(text, inv)) { const fixed = serviceizeLocation(text, inv); if (!fixed) continue; text = fixed; }
    // Whole words only — a service sitelink must never ship a cut-off fragment.
    if (text.length > SITELINK_TEXT_MAX) text = fitWholeWords(text, SITELINK_TEXT_MAX);
    if (!text || usedTexts.has(text.toLowerCase())) continue;
    const kind: "specific" | "main" = locUrls.has(normUrl(p.url)) ? "specific" : "main";
    return { text, url: p.url, kind };
  }
  return null;
}

export function validateSitelink(s: Sitelink): string[] {
  const issues: string[] = [];
  if (!s.text) issues.push("missing link text");
  if (s.text.length > SITELINK_TEXT_MAX) issues.push(`link text ${s.text.length}/${SITELINK_TEXT_MAX} chars: “${s.text}”`);
  if (!s.url) issues.push("missing/invalid URL");
  return issues;
}
export const sitelinkTextTooLong = (t: string) => (t?.length || 0) > SITELINK_TEXT_MAX;

// Normalize a whole workspace object (only type:"ad" gets restructured). `inv` gates every
// sitelink URL to a verified real page.
export function normalizeWorkspace(ws: any, inv?: SiteInventory | null): any {
  if (!ws || ws.type !== "ad") return ws;
  return { ...ws, payload: { adGroups: normalizeAdGroups(ws.payload, inv) } };
}

// ── Validation against the hard limits (empty issues array = valid) ──
export function validateAd(ad: RSA): string[] {
  const issues: string[] = [];
  const hls = ad.headlines ?? [];
  const dsc = ad.descriptions ?? [];
  if (hls.length < HEADLINES_MIN) issues.push(`Needs at least ${HEADLINES_MIN} headlines (has ${hls.length}).`);
  if (hls.length > HEADLINES_MAX) issues.push(`Too many headlines: ${hls.length} (max ${HEADLINES_MAX}).`);
  hls.forEach((h, i) => { if (h.length > HEADLINE_MAX) issues.push(`Headline ${i + 1} is ${h.length} chars (max ${HEADLINE_MAX}): “${h}”`); });
  if (dsc.length < DESCRIPTIONS_MIN) issues.push(`Needs at least ${DESCRIPTIONS_MIN} descriptions (has ${dsc.length}).`);
  if (dsc.length > DESCRIPTIONS_MAX) issues.push(`Too many descriptions: ${dsc.length} (max ${DESCRIPTIONS_MAX}).`);
  dsc.forEach((d, i) => { if (d.length > DESC_MAX) issues.push(`Description ${i + 1} is ${d.length} chars (max ${DESC_MAX}): “${d}”`); });
  if (!ad.url) issues.push("Missing final URL.");
  return issues;
}

export function validateAdGroup(g: AdGroupSpec): string[] {
  const issues: string[] = [];
  if ((g.ads ?? []).length === 0) issues.push("No ads in this ad group.");
  if ((g.ads ?? []).length > ADS_PER_GROUP_MAX) issues.push(`Too many ads: ${g.ads.length} (Google allows max ${ADS_PER_GROUP_MAX} per ad group).`);
  g.ads?.forEach((ad, i) => validateAd(ad).forEach((iss) => issues.push(`Ad ${i + 1}: ${iss}`)));
  g.sitelinks?.forEach((s, i) => validateSitelink(s).forEach((iss) => issues.push(`Sitelink ${i + 1}: ${iss}`)));
  return issues;
}

// Total counts across all ad groups (sitelinks now live per ad group). Confirm-gate summary.
export function scopeTotals(adGroups: AdGroupSpec[]) {
  return adGroups.reduce(
    (acc, g) => {
      acc.adGroups += 1;
      acc.ads += (g.ads ?? []).length;
      acc.keywords += (g.keywords ?? []).length;
      acc.sitelinks += (g.sitelinks ?? []).length;
      acc.issues += validateAdGroup(g).length;
      return acc;
    },
    { adGroups: 0, ads: 0, keywords: 0, sitelinks: 0, issues: 0 },
  );
}

export const headlineTooLong = (h: string) => (h?.length || 0) > HEADLINE_MAX;
export const descTooLong = (d: string) => (d?.length || 0) > DESC_MAX;
